/**
 * ETL "data flowing" edge (ADR-0038) — the Fluxo designer's signature touch.
 *
 * A normal bezier edge that, while `data.flowing` is true, turns emerald
 * (matching the running-node glow) and sends a STREAM of small dots travelling
 * down the path via SMIL `<animateMotion>` — so a live run literally SHOWS the
 * data moving block → block. When not flowing it renders identically to the
 * default edge (so it's safe to stamp on EVERY edge, run or not).
 *
 * Density + speed:
 *  - `_DOT_COUNT` dots ride the same path, evenly spaced via NEGATIVE `begin`
 *    offsets (SMIL treats a negative begin as "already running", so each dot
 *    starts a fraction of the cycle ahead → a continuous stream, not a blink).
 *  - `data.durSec` (seconds per traversal) drives speed: the editor derives it
 *    from the source block's throughput, so a high-volume edge visibly RUSHES.
 *
 * `animateMotion` takes the path `d` string directly (no `<mpath>` ref), so the
 * stream re-derives its track whenever ReactFlow re-routes the edge.
 */
import { BaseEdge, getBezierPath, type EdgeProps } from "reactflow";

const FLOW_COLOR = "#34d399"; // emerald-400 — same hue as the running-node ring
const _DOT_COUNT = 4;
const _DEFAULT_DUR = 1.4;

export function EtlFlowEdge({
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  markerEnd,
  style,
  data,
}: EdgeProps) {
  const [edgePath] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });
  const d = data as { flowing?: boolean; durSec?: number } | undefined;
  const flowing = Boolean(d?.flowing);
  const dur = Math.max(0.3, d?.durSec ?? _DEFAULT_DUR);

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          ...style,
          ...(flowing
            ? { stroke: FLOW_COLOR, strokeWidth: 2, opacity: 0.9 }
            : {}),
        }}
      />
      {flowing &&
        Array.from({ length: _DOT_COUNT }, (_, i) => (
          <circle key={i} r={3.5} fill={FLOW_COLOR}>
            <animateMotion
              dur={`${dur}s`}
              repeatCount="indefinite"
              path={edgePath}
              // Stagger each dot a fraction of a cycle ahead → an even stream.
              begin={`-${((dur * i) / _DOT_COUNT).toFixed(3)}s`}
            />
          </circle>
        ))}
    </>
  );
}
